import { Building2, Dumbbell, GraduationCap, Plane, ShoppingBag, type LucideIcon } from 'lucide-react';

export type VenueType = 'gym' | 'campus' | 'transit' | 'retail' | 'other';

export type Approver =
  | 'estates_facilities'
  | 'procurement'
  | 'catering_retail'
  | 'center_management'
  | 'landlord_owner'
  | 'leasing_team'
  | 'other';

export const SITES_OPTIONS = ['1-10', '11-50', '51-249', '250+'] as const;
export type SitesCount = (typeof SITES_OPTIONS)[number];

export interface Answers {
  venueType?: VenueType;

  venueName: string;
  address: string;
  addressLat?: number;
  addressLon?: number;
  sitesCount?: SitesCount;
  securitySide?: 'landside' | 'airside' | 'both';

  totalMembers: string;
  estateDailyVisitors: string;
  dailyVisitors: string;
  machineCount: string;
  machineLocation: string;
  sensorsOk?: 'yes' | 'check' | 'no';

  productRestrictions?: 'yes' | 'no';
  productLimits: string;
  adRestrictions?: 'yes' | 'no';
  restrictedCategories: string;
  tenantExclusivity?: 'yes' | 'no' | 'not_sure';
  termYears?: '3' | '4' | '5';
  tenure?: 'own' | 'lease' | 'both';
  leaseThreePlus?: 'yes' | 'no' | 'varies';
  approvalNeeded?: 'yes' | 'no' | 'not_sure';
  locationControl?: 'university' | 'third_party' | 'not_sure';
  approvers: Approver[];

  contactName: string;
  contactRole?: string;
  email: string;
  phone: string;
  canSign?: 'yes' | 'other';
  signerName: string;
  signerEmail: string;
  currentIncome: string;
  exploringReason: string;
}

export interface Photo {
  id: string;
  name: string;
  dataUrl: string;
}

export const emptyAnswers: Answers = {
  venueName: '',
  address: '',
  totalMembers: '',
  estateDailyVisitors: '',
  dailyVisitors: '',
  productLimits: '',
  machineCount: '',
  machineLocation: '',
  restrictedCategories: '',
  approvers: [],
  contactName: '',
  email: '',
  phone: '',
  signerName: '',
  signerEmail: '',
  currentIncome: '',
  exploringReason: '',
};

export const STEPS = ['type', 'where', 'busy', 'spot', 'products', 'agreement', 'contact'] as const;
export type StepId = (typeof STEPS)[number];

export interface VenueTypeOption {
  id: VenueType;
  label: string;
  hint: string;
  icon: LucideIcon;
}

export const VENUE_TYPES: VenueTypeOption[] = [
  { id: 'gym', label: 'Health and Fitness', hint: 'Gyms, health clubs and padel', icon: Dumbbell },
  { id: 'campus', label: 'Education', hint: 'Universities, campuses and schools', icon: GraduationCap },
  { id: 'transit', label: 'Transportation', hint: 'Airports and train stations', icon: Plane },
  { id: 'retail', label: 'Retail', hint: 'Shopping centers, malls and stores', icon: ShoppingBag },
  { id: 'other', label: 'Other', hint: 'Offices, hospitals, hotels and more', icon: Building2 },
];

const TYPE_ALIASES: Record<string, VenueType> = {
  gym: 'gym',
  club: 'gym',
  fitness: 'gym',
  padel: 'gym',
  transportation: 'transit',
  campus: 'campus',
  education: 'campus',
  university: 'campus',
  college: 'campus',
  airport: 'transit',
  station: 'transit',
  transit: 'transit',
  mall: 'retail',
  store: 'retail',
  retail: 'retail',
  other: 'other',
};

export function venueTypeFromParam(value: string | null): VenueType | undefined {
  return value ? TYPE_ALIASES[value.trim().toLowerCase()] : undefined;
}

export const ROLE_OPTIONS = ['Owner / Founder', 'Executive / Director', 'Operations / Facilities', 'Other'];

export function parseVisitors(raw: string): number {
  const n = parseInt(raw.replace(/[^0-9]/g, ''), 10);
  return Number.isFinite(n) ? n : 0;
}

export const formatNumber = (n: number) => n.toLocaleString('en-US');

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type Errors = Partial<Record<keyof Answers, string>>;

export function validateStep(step: StepId, a: Answers): Errors {
  const e: Errors = {};
  const require = (key: keyof Answers, ok: unknown, message = 'Pick one to continue') => {
    if (!ok) e[key] = message;
  };
  switch (step) {
    case 'type':
      require('venueType', a.venueType);
      break;
    case 'where':
      require('venueName', a.venueName.trim(), 'Enter the venue name');
      require('address', a.address.trim(), 'Enter the address');
      require('sitesCount', a.sitesCount);
      if (a.venueType === 'transit') require('securitySide', a.securitySide);
      break;
    case 'busy':
      require('totalMembers', parseVisitors(a.totalMembers) > 0, 'Enter a number');
      if (a.venueType === 'gym') require('estateDailyVisitors', parseVisitors(a.estateDailyVisitors) > 0, 'Enter a number');
      require('dailyVisitors', parseVisitors(a.dailyVisitors) > 0, 'Enter a number');
      break;
    case 'spot':
      require('machineCount', parseVisitors(a.machineCount) > 0, 'Enter a number');
      require('sensorsOk', a.sensorsOk);
      require('machineLocation', a.machineLocation.trim(), 'Tell us where the machines would go');
      break;
    case 'products':
      require('productRestrictions', a.productRestrictions);
      if (a.productRestrictions === 'yes') require('productLimits', a.productLimits.trim(), 'Tell us what they are');
      require('adRestrictions', a.adRestrictions);
      if (a.adRestrictions === 'yes') require('restrictedCategories', a.restrictedCategories.trim(), 'Tell us what they are');
      if (a.venueType === 'retail') require('tenantExclusivity', a.tenantExclusivity);
      break;
    case 'agreement':
      require('termYears', a.termYears);
      if (a.venueType === 'campus') {
        require('locationControl', a.locationControl);
        require('approvalNeeded', a.approvalNeeded);
        if (a.approvalNeeded === 'yes') require('approvers', a.approvers.length > 0, 'Pick at least one');
      } else {
        require('tenure', a.tenure);
        if (a.tenure === 'lease' || a.tenure === 'both') require('leaseThreePlus', a.leaseThreePlus);
        require('approvalNeeded', a.approvalNeeded);
        if (a.venueType === 'retail' && a.approvalNeeded === 'yes') require('approvers', a.approvers.length > 0, 'Pick at least one');
      }
      break;
    case 'contact':
      require('contactName', a.contactName.trim(), 'Enter your name');
      require('contactRole', a.contactRole);
      require('email', EMAIL_RE.test(a.email.trim()), 'Enter a valid email');
      require('phone', a.phone.replace(/[^0-9]/g, '').length >= 7, 'Enter a valid phone number');
      require('canSign', a.canSign);
      if (a.canSign === 'other') {
        require('signerName', a.signerName.trim(), "Enter the signer's name");
        require('signerEmail', EMAIL_RE.test(a.signerEmail.trim()), "Enter the signer's email");
      }
      require('currentIncome', a.currentIncome.trim(), 'Enter a number, or 0 if none');
      require('exploringReason', a.exploringReason.trim(), 'Tell us why you are exploring connected retail');
      break;
  }
  return e;
}

export function toPayload(a: Answers, photos: Photo[]) {
  const type = a.venueType as VenueType;
  return {
    venueType: type,
    venueName: a.venueName.trim(),
    address: a.address.trim(),
    addressLat: a.addressLat,
    addressLon: a.addressLon,
    sitesCount: a.sitesCount,
    securitySide: type === 'transit' ? a.securitySide : undefined,
    totalMembers: parseVisitors(a.totalMembers),
    estateDailyVisitors: type === 'gym' ? parseVisitors(a.estateDailyVisitors) : undefined,
    dailyVisitors: parseVisitors(a.dailyVisitors),
    machineCount: parseVisitors(a.machineCount),
    sensorsOk: a.sensorsOk,
    machineLocation: a.machineLocation.trim(),
    photos: photos.map(({ name, dataUrl }) => ({ name, dataUrl })),
    productRestrictions: a.productRestrictions,
    productLimits: a.productRestrictions === 'yes' ? a.productLimits.trim() : '',
    adRestrictions: a.adRestrictions,
    restrictedCategories: a.adRestrictions === 'yes' ? a.restrictedCategories.trim() : '',
    tenantExclusivity: type === 'retail' ? a.tenantExclusivity : undefined,
    termYears: a.termYears,
    tenure: type === 'campus' ? undefined : a.tenure,
    leaseThreePlus: type !== 'campus' && (a.tenure === 'lease' || a.tenure === 'both') ? a.leaseThreePlus : undefined,
    locationControl: type === 'campus' ? a.locationControl : undefined,
    approvalNeeded: a.approvalNeeded,
    approvers: (type === 'campus' || type === 'retail') && a.approvalNeeded === 'yes' ? a.approvers : [],
    contactName: a.contactName.trim(),
    contactRole: a.contactRole,
    email: a.email.trim(),
    phone: a.phone.trim(),
    canSign: a.canSign,
    signerName: a.canSign === 'other' ? a.signerName.trim() : '',
    signerEmail: a.canSign === 'other' ? a.signerEmail.trim() : '',
    currentIncome: parseVisitors(a.currentIncome),
    exploringReason: a.exploringReason.trim(),
  };
}
